import React, { useState, useRef, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { FaUsers, FaTimes, FaPaperPlane, FaTrash, FaSync, FaMagic, FaRobot, FaEnvelope, FaSearch, FaUser, FaUserPlus, FaPlus, FaBan, FaCheckCircle } from 'react-icons/fa';
import { getGroupChatMessages, sendGroupChatMessage, deleteGroupChatMessage, getGroupChatMembers, applyGroupChatMessage, sendChatMessage, discardChatModification, inviteMemberByEmail, searchUsers } from '../api/client';
import ConfirmDialog from './ConfirmDialog';
import './GroupChat.css';

// Sub-component for modification selection with checkboxes in group chat
const GroupChatModSelector = ({ modifications, onConfirm, onSkipAll }) => {
  const [selected, setSelected] = React.useState(modifications.map(() => true));
  const toggleItem = (i) => setSelected((prev) => prev.map((v, j) => j === i ? !v : v));
  const selectedCount = selected.filter(Boolean).length;

  return (
    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px', marginTop: '8px' }}>
      <p style={{ fontSize: '11px', color: '#475569', margin: '0 0 8px', fontWeight: 600 }}>
        {modifications.length > 1 ? 'Select stops to add:' : 'Add this stop to itinerary?'}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
        {modifications.map((mod, i) => (
          <label key={mod.id} style={{
            display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px',
            background: selected[i] ? '#dcfce7' : '#ffffff',
            border: `2px solid ${selected[i] ? '#22c55e' : '#d1d5db'}`,
            borderRadius: '6px', cursor: 'pointer', fontSize: '12px', lineHeight: '1.3',
            transition: 'all 0.2s',
            boxShadow: selected[i] ? '0 1px 3px rgba(34, 197, 94, 0.15)' : 'none'
          }}>
            <input type="checkbox" checked={selected[i]} onChange={() => toggleItem(i)}
              style={{
                accentColor: '#22c55e',
                width: '18px',
                height: '18px',
                minWidth: '18px',
                minHeight: '18px',
                flexShrink: 0,
                cursor: 'pointer',
                appearance: 'auto',
                WebkitAppearance: 'checkbox',
                border: '2px solid #22c55e',
                borderRadius: '4px'
              }} />
            <span style={{ color: selected[i] ? '#166534' : '#6b7280', fontWeight: selected[i] ? 500 : 400 }}>{mod.description}</span>
          </label>
        ))}
      </div>
      <div className="ai-action-buttons">
        <button className="ai-apply-btn" onClick={() => onConfirm(selected)} disabled={selectedCount === 0}
          style={{ opacity: selectedCount === 0 ? 0.5 : 1 }}>
          Add {selectedCount > 1 ? `${selectedCount} Stops` : selectedCount === 1 ? 'to Itinerary' : 'None'}
        </button>
        <button className="ai-cancel-btn" onClick={onSkipAll}>Skip</button>
      </div>
    </div>
  );
};

// Format chat message text into structured HTML with bullet lists and bold
const formatMessageText = (text) => {
  if (!text) return '';
  const lines = text.split('\n');
  let html = '';
  let inList = false;
  let listType = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const ulMatch = line.match(/^\s*[-*•]\s+(.*)/);
    const olMatch = line.match(/^\s*(\d+)[.)]\s+(.*)/);

    if (ulMatch) {
      if (!inList || listType !== 'ul') {
        if (inList) html += listType === 'ol' ? '</ol>' : '</ul>';
        html += '<ul>';
        inList = true;
        listType = 'ul';
      }
      html += `<li>${ulMatch[1]}</li>`;
    } else if (olMatch) {
      if (!inList || listType !== 'ol') {
        if (inList) html += listType === 'ol' ? '</ol>' : '</ul>';
        html += '<ol>';
        inList = true;
        listType = 'ol';
      }
      html += `<li>${olMatch[2]}</li>`;
    } else {
      if (inList) {
        html += listType === 'ol' ? '</ol>' : '</ul>';
        inList = false;
        listType = null;
      }
      if (line.trim() === '') {
        html += '<br/>';
      } else {
        html += `<p>${line}</p>`;
      }
    }
  }
  if (inList) html += listType === 'ol' ? '</ol>' : '</ul>';

  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  return DOMPurify.sanitize(html);
};

// Get WebSocket URL from environment or derive from API URL
const getWebSocketUrl = () => {
  const runtimeConfig = window.__RUNTIME_CONFIG__ || {};
  const apiUrl = runtimeConfig.API_URL || process.env.REACT_APP_API_URL || `${window.location.origin}/api`;
  const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
  const baseUrl = `${wsProtocol}://${window.location.host}`;
  return baseUrl;
};

const GroupChat = ({ requestId, isOwner, onModificationApplied }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [applyingMessageId, setApplyingMessageId] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showMembers, setShowMembers] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [pendingConfirmation, setPendingConfirmation] = useState(null); // { messageId, messageContent }
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ isOpen: false, messageId: null });
  const [aiMessages, setAiMessages] = useState([]); // AI conversation messages shown inline
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef(null);

  // Draggable window
  const handleDragStart = (e) => {
    if (e.target.tagName === 'BUTTON' || e.target.closest('button')) return;
    const rect = dragRef.current.getBoundingClientRect();
    const startX = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const startY = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;

    const handleMove = (ev) => {
      const cx = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const cy = ev.touches ? ev.touches[0].clientY : ev.clientY;
      const newX = cx - startX;
      const newY = cy - startY;
      const maxX = window.innerWidth - dragRef.current.offsetWidth;
      const maxY = window.innerHeight - dragRef.current.offsetHeight;
      dragRef.current.style.left = `${Math.max(0, Math.min(newX, maxX))}px`;
      dragRef.current.style.top = `${Math.max(0, Math.min(newY, maxY))}px`;
      dragRef.current.style.right = 'auto';
      dragRef.current.style.bottom = 'auto';
      setIsDragging(true);
    };
    const handleEnd = () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
      setTimeout(() => setIsDragging(false), 0);
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
  };

  // Invite member states
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteIncludePhotos, setInviteIncludePhotos] = useState(true);
  const [inviteAdding, setInviteAdding] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [inviteSearchResults, setInviteSearchResults] = useState([]);
  const [inviteSearchLoading, setInviteSearchLoading] = useState(false);
  const [inviteShowDropdown, setInviteShowDropdown] = useState(false);
  const inviteInputRef = useRef(null);
  const inviteDropdownRef = useRef(null);
  const inviteSearchTimeoutRef = useRef(null);
  const [isAiLoading, setIsAiLoading] = useState(false);
  // pendingAiModification removed - changes are now queued
  const [showTooltip, setShowTooltip] = useState(false);

  // Show tooltip for first-time users
  useEffect(() => {
    const hasSeenTooltip = localStorage.getItem('group_chat_tooltip_seen');
    if (!hasSeenTooltip) {
      setShowTooltip(true);
    }
  }, []);

  const dismissTooltip = () => {
    setShowTooltip(false);
    localStorage.setItem('group_chat_tooltip_seen', 'true');
  };
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingStatusRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectDelay = 5000; // Max 5 seconds between reconnects

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = useCallback(async (showLoading = true) => {
    if (showLoading) setIsLoading(true);
    setError(null);
    try {
      const data = await getGroupChatMessages(requestId);
      setMessages(data.messages || []);
    } catch (err) {
      console.error('Error fetching messages:', err);
      if (showLoading) {
        setError('Failed to load messages');
      }
    } finally {
      if (showLoading) setIsLoading(false);
    }
  }, [requestId]);

  const fetchMembers = useCallback(async () => {
    try {
      const data = await getGroupChatMembers(requestId);
      setMembers(data.members || []);
    } catch (err) {
      console.error('Error fetching members:', err);
    }
  }, [requestId]);

  // Invite member handlers
  const performInviteSearch = useCallback(async (query) => {
    if (!query || query.length < 1) {
      setInviteSearchResults([]);
      setInviteShowDropdown(false);
      setInviteSearchLoading(false);
      return;
    }
    setInviteSearchLoading(true);
    try {
      const response = await searchUsers(query);
      const memberEmails = members.map(m => m.email?.toLowerCase());
      const filtered = (response.results || []).filter(
        user => !memberEmails.includes(user.email?.toLowerCase())
      );
      setInviteSearchResults(filtered);
      setInviteShowDropdown(filtered.length > 0);
    } catch (err) {
      console.error('Error searching users:', err);
      setInviteSearchResults([]);
    } finally {
      setInviteSearchLoading(false);
    }
  }, [members]);

  const handleInviteEmailChange = (e) => {
    const value = e.target.value;
    setInviteEmail(value);
    if (value.length >= 1) setInviteSearchLoading(true);
    if (inviteSearchTimeoutRef.current) clearTimeout(inviteSearchTimeoutRef.current);
    inviteSearchTimeoutRef.current = setTimeout(() => performInviteSearch(value), 50);
  };

  const handleInviteSelectUser = (user) => {
    setInviteEmail(user.email);
    setInviteShowDropdown(false);
    setInviteSearchResults([]);
  };

  const handleInviteMember = async () => {
    if (!inviteEmail) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      setInviteError('Please enter a valid email address');
      return;
    }
    try {
      setInviteAdding(true);
      setInviteError('');
      const result = await inviteMemberByEmail(requestId, inviteEmail, inviteIncludePhotos);
      setInviteSuccess(result.status === 'active' ? 'Member added!' : 'Invitation sent!');
      setInviteEmail('');
      fetchMembers();
      setTimeout(() => setInviteSuccess(''), 3000);
    } catch (err) {
      setInviteError(err.response?.data?.detail || 'Failed to invite member');
    } finally {
      setInviteAdding(false);
    }
  };

  // Close invite dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inviteDropdownRef.current && !inviteDropdownRef.current.contains(event.target) &&
        inviteInputRef.current && !inviteInputRef.current.contains(event.target)
      ) {
        setInviteShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // WebSocket connection for real-time messages
  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('access_token');
    if (!token || !requestId) return;

    const wsUrl = `${getWebSocketUrl()}/ws/chat/${requestId}?token=${token}`;

    try {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('Chat WebSocket connected');
        reconnectAttemptsRef.current = 0; // Reset reconnect attempts on successful connection
        // Start ping interval to keep connection alive (every 15 seconds)
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send('ping');
          }
        }, 15000);
      };

      wsRef.current.onmessage = (event) => {
        if (event.data === 'pong') return; // Ignore pong responses

        try {
          const data = JSON.parse(event.data);
          if (data.type === 'new_message') {
            // Skip own messages - they're added via API response in handleSendMessage
            if (data.data.is_own_message) {
              return;
            }
            // Add new message only if it doesn't already exist
            setMessages((prev) => {
              const messageExists = prev.some((m) => m.id === data.data.id);
              if (messageExists) {
                return prev; // Don't add duplicate
              }
              return [...prev, data.data];
            });
            // Play soft chime for new message from others
            try {
              const audioContext = new (window.AudioContext || window.webkitAudioContext)();
              const oscillator = audioContext.createOscillator();
              const gainNode = audioContext.createGain();
              oscillator.connect(gainNode);
              gainNode.connect(audioContext.destination);
              oscillator.frequency.setValueAtTime(600, audioContext.currentTime);
              oscillator.type = 'sine';
              gainNode.gain.setValueAtTime(0.15, audioContext.currentTime);
              gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
              oscillator.start(audioContext.currentTime);
              oscillator.stop(audioContext.currentTime + 0.2);
            } catch (audioErr) {
              // Silently fail if audio can't play
            }
          } else if (data.type === 'message_deleted') {
            // Remove deleted message
            setMessages((prev) => prev.filter((m) => m.id !== data.data.message_id));
          } else if (data.type === 'typing_status') {
            // Update typing users
            setTypingUsers(data.data.typing_users || []);
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log('Chat WebSocket closed:', event.code);
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        // Fast reconnect with exponential backoff (100ms, 200ms, 400ms, ... up to 5s)
        if (isOpen && event.code !== 4001 && event.code !== 4003) {
          const delay = Math.min(100 * Math.pow(2, reconnectAttemptsRef.current), maxReconnectDelay);
          reconnectAttemptsRef.current++;
          reconnectTimeoutRef.current = setTimeout(connectWebSocket, delay);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('Chat WebSocket error:', error);
        // Immediately try to reconnect on error
        if (wsRef.current) {
          wsRef.current.close();
        }
      };
    } catch (e) {
      console.error('Failed to create WebSocket:', e);
    }
  }, [requestId, isOpen]);

  const disconnectWebSocket = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setTypingUsers([]);
  }, []);

  // Send typing status via WebSocket
  const sendTypingStatus = useCallback((isTyping) => {
    if (wsRef.current?.readyState === WebSocket.OPEN && lastTypingStatusRef.current !== isTyping) {
      lastTypingStatusRef.current = isTyping;
      wsRef.current.send(JSON.stringify({ type: 'typing', is_typing: isTyping }));
    }
  }, []);

  // Handle input change with typing indicator
  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setInputValue(value);

    // Send typing status
    if (value.trim()) {
      sendTypingStatus(true);
      // Clear previous timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      // Stop typing after 2 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        sendTypingStatus(false);
      }, 2000);
    } else {
      sendTypingStatus(false);
    }
  }, [sendTypingStatus]);

  useEffect(() => {
    if (isOpen) {
      fetchMessages();
      fetchMembers();
      // Connect WebSocket for real-time updates
      connectWebSocket();

      // Reconnect immediately when tab becomes visible
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible' && isOpen) {
          // Check if WebSocket is disconnected and reconnect immediately
          if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
            reconnectAttemptsRef.current = 0; // Reset for immediate reconnect
            connectWebSocket();
          }
          // Refresh messages in case any were missed
          fetchMessages(false);
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        disconnectWebSocket();
      };
    } else {
      disconnectWebSocket();
    }
  }, [isOpen, fetchMessages, fetchMembers, connectWebSocket, disconnectWebSocket]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isSending || isAiLoading) return;

    const messageText = inputValue.trim();
    setInputValue('');
    sendTypingStatus(false);

    // Check if message is directed at AI (starts with @artie)
    const aiMatch = messageText.match(/^@artie\s+([\s\S]+)/i);
    if (aiMatch) {
      const aiQuery = aiMatch[1].trim();
      // Add user's AI query as an AI-prefixed message in the local AI thread
      setAiMessages((prev) => [...prev, { type: 'user', content: aiQuery }]);
      setIsAiLoading(true);

      try {
        const data = await sendChatMessage(requestId, aiQuery);
        const { response: aiResponse, modification_description, modification_id, modifications } = data;

        // Debug logging
        console.log('GroupChat Artie response:', { aiResponse, modification_description, modification_id, modifications });

        // Determine if there's a valid modification to show
        const hasValidModification = modification_description &&
          modification_description.trim() !== '' &&
          modification_description.toLowerCase() !== 'no modification needed' &&
          modification_description.toLowerCase() !== 'none' &&
          !modification_description.toLowerCase().includes('no modification');

        setAiMessages((prev) => [
          ...prev,
          {
            type: 'ai',
            content: aiResponse,
            modificationDescription: modification_description,
            modificationId: modification_id,
            modifications: modifications || (
              hasValidModification
                ? [{ id: modification_id, description: modification_description }]
                : null
            ),
          },
        ]);
      } catch (err) {
        console.error('Error sending AI message:', err);
        const errorMsg = err.response?.data?.detail || err.message || 'AI assistant encountered an error.';
        setAiMessages((prev) => [...prev, { type: 'error', content: errorMsg }]);
      } finally {
        setIsAiLoading(false);
      }
      return;
    }

    // Regular group message
    setIsSending(true);
    try {
      const newMessage = await sendGroupChatMessage(requestId, messageText);
      setMessages((prev) => {
        const messageExists = prev.some((m) => m.id === newMessage.id);
        if (messageExists) {
          return prev.map((m) => m.id === newMessage.id ? newMessage : m);
        }
        return [...prev, newMessage];
      });
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message');
      setInputValue(messageText);
    } finally {
      setIsSending(false);
    }
  };

  const handleConfirmAiSelections = async (msgIndex, modifications, selected) => {
    const skippedIds = modifications.filter((_, i) => !selected[i]).map((m) => m.id);
    const selectedCount = selected.filter(Boolean).length;
    for (const id of skippedIds) {
      try { await discardChatModification(requestId, id); } catch (e) { /* ignore */ }
    }
    setAiMessages((prev) => prev.map((m, i) =>
      i === msgIndex ? { ...m, changeStatus: selectedCount > 0 ? 'accepted' : 'skipped', selectedCount } : m
    ));
    if (selectedCount > 0 && onModificationApplied) onModificationApplied('queued');
  };

  const handleSkipAllAi = async (msgIndex, modifications) => {
    for (const mod of modifications) {
      try { await discardChatModification(requestId, mod.id); } catch (e) { /* ignore */ }
    }
    setAiMessages((prev) => prev.map((m, i) =>
      i === msgIndex ? { ...m, changeStatus: 'skipped' } : m
    ));
  };

  const handleDeleteMessage = (messageId) => {
    setDeleteConfirmDialog({ isOpen: true, messageId });
  };

  const confirmDeleteMessage = async () => {
    const messageId = deleteConfirmDialog.messageId;
    setDeleteConfirmDialog({ isOpen: false, messageId: null });

    try {
      await deleteGroupChatMessage(requestId, messageId);
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
    } catch (err) {
      console.error('Error deleting message:', err);
      setError('Failed to delete message');
    }
  };

  const handleApplyMessage = async (messageId, messageContent) => {
    if (!isOwner) return;

    // Send message to Scout to parse into individual place suggestions
    setApplyingMessageId(messageId);
    setError(null);

    try {
      const data = await sendChatMessage(requestId, `The following is a group member's suggestion for our trip. Parse it into individual actionable modifications:\n\n"${messageContent}"`);
      const { response: aiResponse, modification_description, modification_id, modifications } = data;

      const parsedMods = modifications || (
        modification_description && modification_description !== 'No modification needed' && modification_description.toLowerCase() !== 'none'
          ? [{ id: modification_id, description: modification_description }]
          : null
      );

      if (parsedMods && parsedMods.length > 0) {
        // Show inline selector for this message
        setPendingConfirmation({ messageId, messageContent, modifications: parsedMods, aiResponse });
      } else {
        // No modifications detected, apply directly
        await applyGroupChatMessage(requestId, messageId);
        setSuccessMessage('Suggestion applied! Your itinerary is being regenerated.');
        if (onModificationApplied) onModificationApplied();
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (err) {
      console.error('Error parsing message:', err);
      // Fallback: show simple confirmation
      setPendingConfirmation({ messageId, messageContent, modifications: null });
    } finally {
      setApplyingMessageId(null);
    }
  };

  const handleConfirmApply = async () => {
    if (!pendingConfirmation) return;

    const { messageId, modifications } = pendingConfirmation;

    // If no modifications (fallback), apply directly
    if (!modifications) {
      setPendingConfirmation(null);
      setApplyingMessageId(messageId);
      setError(null);
      setSuccessMessage(null);

      try {
        await applyGroupChatMessage(requestId, messageId);
        setSuccessMessage('Suggestion applied! Your itinerary is being regenerated to include this change. It will refresh automatically.');
        if (onModificationApplied) onModificationApplied();
        setTimeout(() => setSuccessMessage(null), 5000);
      } catch (err) {
        console.error('Error applying message:', err);
        const errorMsg = err.response?.data?.detail || 'Failed to apply suggestion';
        setError(errorMsg);
      } finally {
        setApplyingMessageId(null);
      }
      return;
    }

    // Otherwise handled by handleConfirmGroupSelections
  };

  const handleConfirmGroupSelections = async (selected) => {
    if (!pendingConfirmation?.modifications) return;

    const { modifications } = pendingConfirmation;
    const selectedCount = selected.filter(Boolean).length;
    const skippedIds = modifications.filter((_, i) => !selected[i]).map((m) => m.id);

    // Discard unselected modifications
    for (const id of skippedIds) {
      try { await discardChatModification(requestId, id); } catch (e) { /* ignore */ }
    }

    setPendingConfirmation(null);

    if (selectedCount > 0) {
      setSuccessMessage(`${selectedCount} change${selectedCount > 1 ? 's' : ''} added to pending — regenerate when ready`);
      if (onModificationApplied) onModificationApplied('queued');
    }
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const handleSkipAllGroup = async () => {
    if (!pendingConfirmation?.modifications) return;

    const { modifications } = pendingConfirmation;
    for (const mod of modifications) {
      try { await discardChatModification(requestId, mod.id); } catch (e) { /* ignore */ }
    }
    setPendingConfirmation(null);
  };

  const handleCancelApply = () => {
    // If there are parsed modifications, discard them all
    if (pendingConfirmation?.modifications) {
      handleSkipAllGroup();
    } else {
      setPendingConfirmation(null);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' }) + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  };

  return (
    <>
      {/* Group Chat Toggle Button with Tooltip */}
      {!isOpen && (
        <div className="group-chat-toggle-container">
          {showTooltip && (
            <div className="group-chat-tooltip">
              <div className="group-chat-tooltip-content">
                <div className="group-chat-tooltip-header">
                  <span className="group-chat-tooltip-icon">&#128172;</span>
                  <strong>Group Chat & Artie AI</strong>
                </div>
                <p>Collaborate with your trip companions and get AI assistance!</p>
                <ul>
                  <li><strong>Chat with your group</strong> - Discuss plans in real-time</li>
                  <li><strong>Type @artie</strong> - Ask the AI for recommendations</li>
                  <li><strong>Apply suggestions</strong> - Click the magic wand to modify your itinerary</li>
                </ul>
                <button className="group-chat-tooltip-dismiss" onClick={dismissTooltip}>
                  Got it!
                </button>
              </div>
              <div className="group-chat-tooltip-arrow"></div>
            </div>
          )}
          <button
            className={`group-chat-toggle-button ${showTooltip ? 'group-chat-toggle-highlight' : ''}`}
            onClick={() => { setIsOpen(true); dismissTooltip(); }}
            title="Group Chat with Trip Members & Artie AI"
          >
            <FaUsers />
            <span className="group-chat-badge">Chat</span>
          </button>
        </div>
      )}

      {/* Group Chat Window */}
      {isOpen && (
        <div className="group-chat-window" ref={dragRef}>
          {/* Header - drag handle */}
          <div className="group-chat-header" onMouseDown={handleDragStart} onTouchStart={handleDragStart} style={{ cursor: 'grab' }}>
            <div className="group-chat-header-left">
              <h3>Group Chat</h3>
              <button
                className="group-chat-members-btn"
                onClick={() => setShowMembers(!showMembers)}
                title="View members"
              >
                <FaUsers /> {members.length}
              </button>
            </div>
            <div className="group-chat-header-right">
              {aiMessages.length > 0 && (
                <button
                  className="group-chat-clear-btn"
                  onClick={(e) => { e.stopPropagation(); setAiMessages([]); }}
                  title="Clear Artie AI chat"
                >
                  <FaTrash />
                </button>
              )}
              <button
                className="group-chat-refresh-btn"
                onClick={(e) => { e.stopPropagation(); fetchMessages(); }}
                disabled={isLoading}
                title="Refresh messages"
              >
                <FaSync className={isLoading ? 'spinning' : ''} />
              </button>
              <button className="group-chat-close-button" onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}>
                <FaTimes />
              </button>
            </div>
          </div>

          {/* Owner hint */}
          {isOwner && (
            <div className="group-chat-owner-hint">
              <FaMagic /> Click the magic wand on any message to apply it to your itinerary. Type <strong>@artie</strong> to ask Artie, your AI travel assistant.
            </div>
          )}

          {/* Non-owner AI hint */}
          {!isOwner && (
            <div className="group-chat-owner-hint">
              <FaRobot /> Type <strong>@artie</strong> followed by your question to ask Artie, your AI travel assistant.
            </div>
          )}

          {/* Members Panel (Collapsible) */}
          {showMembers && (
            <div className="group-chat-members-panel">
              <h4>Trip Members</h4>
              <ul>
                {members.map((member) => (
                  <li key={member.user_id} className={member.is_current_user ? 'current-user' : ''}>
                    <span className="member-name">{member.full_name}</span>
                    <span className="member-role">{member.role}</span>
                    {member.member_code && (
                      <span className="member-code">#{member.member_code}</span>
                    )}
                  </li>
                ))}
              </ul>

              {/* Invite Member Section (owner only) */}
              {isOwner && (
                <div className="group-chat-invite-section">
                  <div className="group-chat-invite-header">
                    <FaUserPlus className="invite-icon" />
                    <span>Invite Member</span>
                  </div>

                  {inviteSuccess && (
                    <div className="group-chat-invite-success">{inviteSuccess}</div>
                  )}
                  {inviteError && (
                    <div className="group-chat-invite-error">
                      {inviteError}
                      <button onClick={() => setInviteError('')}><FaTimes /></button>
                    </div>
                  )}

                  <div className="group-chat-invite-form">
                    <div className="group-chat-invite-input-wrapper">
                      <FaSearch className="invite-search-icon" />
                      <input
                        ref={inviteInputRef}
                        type="email"
                        value={inviteEmail}
                        onChange={handleInviteEmailChange}
                        onFocus={() => inviteEmail.length >= 2 && inviteSearchResults.length > 0 && setInviteShowDropdown(true)}
                        placeholder="Search or enter email..."
                        className="group-chat-invite-input"
                        autoComplete="off"
                      />
                      {inviteSearchLoading && (
                        <div className="invite-search-spinner"></div>
                      )}

                      {/* Typeahead Dropdown */}
                      {inviteShowDropdown && inviteSearchResults.length > 0 && (
                        <div ref={inviteDropdownRef} className="group-chat-invite-dropdown">
                          {inviteSearchResults.map((user) => (
                            <button
                              key={user.id}
                              type="button"
                              onClick={() => handleInviteSelectUser(user)}
                              className="group-chat-invite-dropdown-item"
                            >
                              <div className="invite-dropdown-avatar">
                                <FaUser />
                              </div>
                              <div className="invite-dropdown-info">
                                <span className="invite-dropdown-name">{user.full_name || 'User'}</span>
                                <span className="invite-dropdown-email">{user.email}</span>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={handleInviteMember}
                      disabled={!inviteEmail || inviteAdding}
                      className="group-chat-invite-btn"
                    >
                      {inviteAdding ? '...' : <><FaEnvelope /> Invite</>}
                    </button>
                  </div>

                  <label className="group-chat-invite-photos-toggle">
                    <input
                      type="checkbox"
                      checked={inviteIncludePhotos}
                      onChange={(e) => setInviteIncludePhotos(e.target.checked)}
                    />
                    Allow member to view photos
                  </label>
                </div>
              )}
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="group-chat-success">
              {successMessage}
              <button onClick={() => setSuccessMessage(null)}><FaTimes /></button>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="group-chat-error">
              {error}
              <button onClick={() => setError(null)}><FaTimes /></button>
            </div>
          )}

          {/* Messages */}
          <div className="group-chat-messages">
            {isLoading && messages.length === 0 ? (
              <div className="group-chat-loading">Loading messages...</div>
            ) : messages.length === 0 ? (
              <div className="group-chat-empty">
                <FaUsers size={40} />
                <p>No messages yet</p>
                <p className="group-chat-empty-hint">Start the conversation with your trip members!<br/>Type <strong>@artie</strong> to ask Artie for travel tips and recommendations.</p>
              </div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`group-chat-message ${message.is_own_message ? 'own-message' : 'other-message'}`}
                >
                  {!message.is_own_message && (
                    <div
                      className="message-sender"
                      style={{ color: message.chat_color || '#059669' }}
                    >
                      {message.user_name}
                      {message.member_code && <span className="message-member-code">#{message.member_code}</span>}
                    </div>
                  )}
                  <div
                    className="message-bubble"
                    style={!message.is_own_message && message.chat_color ? {
                      background: message.chat_color,
                      color: 'white'
                    } : undefined}
                  >
                    <div className="message-content" dangerouslySetInnerHTML={{ __html: formatMessageText(message.message) }} />
                    <div className="message-meta">
                      <span className="message-time">{formatTime(message.created_at)}</span>

                      {/* Apply to Itinerary button - only for owner */}
                      {isOwner && (
                        <button
                          className="message-apply-btn"
                          onClick={() => handleApplyMessage(message.id, message.message)}
                          disabled={applyingMessageId === message.id}
                          title="Apply this suggestion to itinerary"
                        >
                          {applyingMessageId === message.id ? (
                            <FaSync className="spinning" />
                          ) : (
                            <FaMagic />
                          )}
                        </button>
                      )}

                      {(message.is_own_message || isOwner) && (
                        <button
                          className="message-delete-btn"
                          onClick={() => handleDeleteMessage(message.id)}
                          title="Delete message"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
            {/* AI Assistant Messages */}
            {aiMessages.length > 0 && (
              <div className="group-chat-ai-section">
                <div className="group-chat-ai-divider">
                  <FaRobot /> Artie AI
                </div>
                {aiMessages.map((msg, idx) => (
                  <div key={`ai-${idx}`} className={`group-chat-ai-message group-chat-ai-${msg.type}`}>
                    {msg.type === 'user' ? (
                      <div className="ai-msg-user">
                        <span className="ai-query-label">You asked:</span>
                        <span>{msg.content}</span>
                      </div>
                    ) : (
                      <div
                        className="ai-msg-content"
                        dangerouslySetInnerHTML={{ __html: formatMessageText(msg.content) }}
                      />
                    )}

                    {msg.type === 'ai' && msg.modifications && msg.modifications.length > 0 && (
                      <div className="ai-modification-actions">
                        {!msg.changeStatus && isOwner && (
                          <GroupChatModSelector
                            modifications={msg.modifications}
                            onConfirm={(selected) => handleConfirmAiSelections(idx, msg.modifications, selected)}
                            onSkipAll={() => handleSkipAllAi(idx, msg.modifications)}
                          />
                        )}
                        {msg.changeStatus === 'accepted' && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', color: '#059669', fontSize: '12px', fontWeight: 600 }}>
                            <FaCheckCircle /> {msg.selectedCount || msg.modifications.length} change{(msg.selectedCount || msg.modifications.length) > 1 ? 's' : ''} added to pending
                          </div>
                        )}
                        {msg.changeStatus === 'skipped' && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', color: '#8A827A', fontSize: '12px' }}>
                            <FaBan /> Skipped
                          </div>
                        )}
                      </div>
                    )}

                    {msg.type === 'subscription' && (
                      <div className="ai-subscription-cta">
                        <a href="/pricing" className="ai-upgrade-btn" onClick={(e) => { e.preventDefault(); window.location.href = '/#pricing'; }}>
                          View Plans & Upgrade
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* AI Loading */}
            {isAiLoading && (
              <div className="group-chat-ai-message group-chat-ai-ai">
                <div className="ai-typing-dots">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Apply Confirmation */}
          {pendingConfirmation && (
            <div className="group-chat-confirmation">
              <div className="confirmation-icon">
                <FaMagic />
              </div>
              <div className="confirmation-content">
                {pendingConfirmation.modifications ? (
                  <>
                    <div className="confirmation-title">Select changes to add</div>
                    <div className="confirmation-message" style={{ marginBottom: '8px' }}>
                      From: "{pendingConfirmation.messageContent.length > 80
                        ? pendingConfirmation.messageContent.substring(0, 80) + '...'
                        : pendingConfirmation.messageContent}"
                    </div>
                    <GroupChatModSelector
                      modifications={pendingConfirmation.modifications}
                      onConfirm={handleConfirmGroupSelections}
                      onSkipAll={handleCancelApply}
                    />
                  </>
                ) : (
                  <>
                    <div className="confirmation-title">Apply this suggestion?</div>
                    <div className="confirmation-message">
                      "{pendingConfirmation.messageContent.length > 80
                        ? pendingConfirmation.messageContent.substring(0, 80) + '...'
                        : pendingConfirmation.messageContent}"
                    </div>
                    <div className="confirmation-note">
                      This will regenerate your itinerary incorporating this suggestion.
                    </div>
                    <div className="confirmation-buttons">
                      <button
                        className="confirmation-btn confirm-btn"
                        onClick={handleConfirmApply}
                      >
                        Yes, Apply
                      </button>
                      <button
                        className="confirmation-btn cancel-btn"
                        onClick={handleCancelApply}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Typing Indicator */}
          {typingUsers.length > 0 && (
            <div className="group-chat-typing-indicator">
              <span className="typing-dots">
                <span></span>
                <span></span>
                <span></span>
              </span>
              <span className="typing-text">
                {typingUsers.length === 1
                  ? `${typingUsers[0]} is typing...`
                  : `${typingUsers.join(', ')} are typing...`}
              </span>
            </div>
          )}

          {/* Input */}
          <div className="group-chat-input-container">
            <textarea
              className="group-chat-input"
              placeholder="Type a message... (use @artie to ask Artie)"
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              rows="2"
              disabled={isSending}
            />
            <button
              className="group-chat-send-button"
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isSending}
            >
              <FaPaperPlane />
            </button>
          </div>
        </div>
      )}

      {/* Delete Message Confirm Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirmDialog.isOpen}
        onClose={() => setDeleteConfirmDialog({ isOpen: false, messageId: null })}
        onConfirm={confirmDeleteMessage}
        title="Delete Message"
        message="Are you sure you want to delete this message? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </>
  );
};

export default GroupChat;
